import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Lock, Eye, EyeOff, Check, AlertCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminResetPage = () => {
  const params = new URLSearchParams(window.location.search);
  const token = params.get("token") || "";

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!token) {
      setError("Missing or invalid reset link. Please request a new one.");
      return;
    }
    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setIsLoading(true);
    try {
      await axios.post(`${API}/admin/reset-password`, {
        token,
        new_password: newPassword
      });
      setSuccess(true);
      setTimeout(() => {
        window.location.href = "/admin";
      }, 2500);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to reset password");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream-50 flex items-center justify-center p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="bg-white shadow-xl overflow-hidden">
          <div className="bg-teal-700 p-6 text-center">
            <h1 className="font-display text-lg tracking-[0.15em] text-cream-50">RESET PASSWORD</h1>
            <p className="font-body text-cream-50/60 text-sm mt-1">The Parris Consulting Group</p>
          </div>

          <div className="p-8">
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 bg-cream-100 rounded-full flex items-center justify-center">
                <Lock className="text-teal-900" size={28} />
              </div>
            </div>

            {success ? (
              <div data-testid="reset-success">
                <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 mb-4 flex items-center gap-2">
                  <Check size={18} />
                  <span className="font-body text-sm">Password reset successfully! Redirecting to sign in...</span>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-testid="reset-password-form">
                <p className="text-center font-body text-teal-900/70 mb-6">
                  Choose a new password for your admin account.
                </p>

                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 mb-4 flex items-center gap-2">
                    <AlertCircle size={18} />
                    <span className="font-body text-sm">{error}</span>
                  </div>
                )}

                <div className="relative mb-5">
                  <label className="font-display text-xs tracking-[0.1em] text-teal-900/60 uppercase mb-2 block">
                    New Password
                  </label>
                  <input
                    type={showPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password (min 8 characters)"
                    className="w-full bg-transparent border-b border-teal-900/20 focus:border-teal-900 py-3 pr-10 outline-none font-body"
                    data-testid="reset-new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-0 bottom-3 text-teal-900/50 hover:text-teal-900"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>

                <div className="mb-6">
                  <label className="font-display text-xs tracking-[0.1em] text-teal-900/60 uppercase mb-2 block">
                    Confirm New Password
                  </label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    className="w-full bg-transparent border-b border-teal-900/20 focus:border-teal-900 py-3 outline-none font-body"
                    data-testid="reset-confirm-password-input"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isLoading || !newPassword || !confirmPassword}
                  className="w-full bg-teal-700 text-cream-50 py-4 font-display text-xs tracking-[0.15em] hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="reset-submit-btn"
                >
                  {isLoading ? "RESETTING..." : "RESET PASSWORD"}
                </button>

                <a
                  href="/admin"
                  className="block text-center mt-6 font-body text-sm text-teal-900/60 hover:text-teal-900"
                >
                  ← Back to sign in
                </a>
              </form>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default AdminResetPage;
